import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ComplexTable } from '@synergy/table';
import { Col, notification, Row, Switch } from 'antd';
import clsx from 'clsx';
import i18next from "i18next";
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import query from '../../../../../server/constants.api';
import useFetch from "../../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../../styles/global/Pages.styled";
import { booleans, period, periodicity, primitiveTypes } from '../../../../../utils/config/widgetsConfig';
import Spinner from '../../../Atomics/Spinner';
import { Text } from '../../../Atomics/Text';
import permissionToEdit from '../AccessUtils';
import { buildObj, getCustomizedButtonsForConfiguration } from '../ConfigurationUtils';
import { EditPopupConfigurations } from '../EditPopupConfigurations';

export const MetricLabelsConfiguration = ({ ...props }) => {
    // Obtém a função de tradução do hook useTranslation para uso na tradução de textos
    const allowedToWrite = permissionToEdit(['Configurations', 'AdvancedMetrics', 'MetricLabelsConfiguration'])
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    let configs;

    const [isTofecth, setIsToFetch] = useState(false);
    const [bodyToPost, setBodyToPost] = useState(null);
    const [isToEdit, setIsToEdit] = useState(false);

    const [idToDelete, setIdToDelete] = useState(null);
    const [isToFetchDelete, setIsToFetchDelete] = useState(false);

    const [isConversionVisible, setIsConversionVisible] = useState(true);

    const {
        data: addOrEditMetric,
        isFetching: addOrEditMetricIsFetching,
        error: addOrEditMetricError,
        isSuccess: addOrEditMetricIsSuccess,
        isError: addOrEditMetricIsError
    } = useFetch("/configurations/advanced/metric-labels", 'editOrAddMetric',
        bodyToPost, isTofecth, isToEdit ? 'PUT' : 'POST', query.middlewareURL);

    const {
        data: metricLabels,
        refetch: refetchMetricList
    } = useFetch("/configurations/advanced/metric-labels", "getMetrics", null, null, null, query.middlewareURL);

    let listingData = serealizeData(metricLabels)

    const {
        data: deleted,
    } = useFetch("/configurations/advanced/metric-labels", "deleteMetricConfig", idToDelete, isToFetchDelete, "DELETE", query.middlewareURL);

    useEffect(() => {
        setIsToFetchDelete(false);
        refetchMetricList();
    }, [deleted])

    useEffect(() => {
        listingData = serealizeData(metricLabels);
    }, [metricLabels])

    const metricLabelConfigurations = [
        {
            title: t('Metric Label'),
            dataIndex: 'Label',
            fieldTypeId: 2,
            required: true
        },
        {
            title: t('Unit Code'),
            dataIndex: 'Unit',
            fieldTypeId: 2,
            required: true
        },
        {
            title: t('Type'),
            dataIndex: 'Type',
            fieldTypeId: 3,
            required: true
        },
        {
            title: t('Is Standard'),
            dataIndex: 'isStandard',
            fieldTypeId: 6,
            customObject: () => {
                return (
                    <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={e => setIsConversionVisible(!e)}
                    />
                )
            }
        },
        {
            title: t('Conversion'),
            dataIndex: 'Conversion',
            fieldTypeId: 1,
            size: 'large',
            isVisible: isConversionVisible
        },
        {
            title: t('Offset'),
            dataIndex: 'Offset',
            isVisible: isConversionVisible
        },
        {
            title: t('Power'),
            dataIndex: 'Power',
            isVisible: isConversionVisible
        },
        {
            title: t('Slope'),
            dataIndex: 'Slope',
            isVisible: isConversionVisible
        },
        {
            title: t('Standard Unit'),
            dataIndex: 'Standard Unit',
            isVisible: isConversionVisible
        },
    ]

    function handleOkModal(selectedRecord, data) {
        const bodyToPost = {
            "Old Label": selectedRecord?.oldLabel,
            "Old Unit": selectedRecord?.oldUnit,
            Label: data?.Label,
            Unit: data?.Unit,
            Power: data?.Power ?? 1,
            Offset: data?.Offset ?? 0,
            Slope: data?.Slope ?? 1,
            "Standard Unit": data['Standard Unit'] ?? data?.Unit,
            Type: data?.Type?.toLowerCase(),
        };
        setBodyToPost(bodyToPost);
        setIsToFetch(true);
        setIsModalOpen(false);
        setSelectedRecord(null);
    }

    function handleEdit(listingData, record) {
        setIsToEdit(true);
        setSelectedRecord(listingData.find(e => e.Label == record.Label && e.Unit == record.Unit));  // Atualiza o estado com o registro selecionado
        if (record?.Power == 1 && record?.Offset == 0 && record?.Slope == 1)
            setIsConversionVisible(false)
        else
            setIsConversionVisible(true)
        setIsModalOpen(true); // Abre a modal
    }


    function handleCloseModal() {
        setIsModalOpen(false);  // Fecha a modal
        setSelectedRecord(null); // Limpa o registro selecionado
        setIsConversionVisible(true)
    }

    useEffect(() => {
        if (addOrEditMetricIsError) {
            const errorMessage = addOrEditMetricError?.response?.data?.detailedMessage || t('An error occurred while saving the metric.');

            // Usar uma notificação para exibir o erro
            notification.error({
                message: t('Error'),
                description: errorMessage,
                duration: 3,  // Define a duração da notificação (3 segundos)
            });
        } else if (addOrEditMetric && !addOrEditMetricIsFetching && addOrEditMetricIsSuccess) {
            refetchMetricList();
            setSelectedRecord(null);
        }
        setIsToFetch(false);
    }, [addOrEditMetricIsFetching, addOrEditMetricIsError]);

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Metric Label List')}</Text>
                                </PageTitle>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        {
                            listingData ?
                                <ComplexTable fetchData={listingData} language={i18next.resolvedLanguage}
                                    tableConfiguration={{ collumnsConfiguration: metricLabelConfigurations.filter(e => e.title !== t("Conversion") && e.title !== t("Is Standard")) }}
                                    editButton={allowedToWrite}
                                    deleteButton={allowedToWrite}
                                    fixedActions={true}
                                    handleDelete={(record) => { setIdToDelete({ label: record.oldLabel, unit: record.oldUnit }); setIsToFetchDelete(true); }}
                                    pagination={{ pageSize: 5 }}
                                    handleEdit={(record) => { handleEdit(listingData, { ...metricEmptyRecord, ...record }) }}
                                    customizedButtons={
                                        getCustomizedButtonsForConfiguration(listingData && allowedToWrite,
                                            () => { setSelectedRecord(metricEmptyRecord); setIsModalOpen(true) }, t)
                                    }
                                />
                                :
                                <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Spinner stroke="green" />
                                </div>
                        }
                    </Col>
                    <EditPopupConfigurations
                        openModal={isModalOpen}
                        isToEdit={isToEdit}
                        closeModal={handleCloseModal}
                        onOkModal={(data) => handleOkModal(selectedRecord, data)}
                        selectedRecord={selectedRecord ? serealizeRecordForModal(selectedRecord, configs, metricLabelConfigurations) : null}
                        customWidth={800} />
                </Row>
            </PageSection>
        </PageContent>
    )
}

//Serializer para tornar a modal no formato correto para editar um User, como uma configuração para a Modal
const serealizeRecordForModal = (selectedRecord, configValues, tableRes) => {
    const keysToKeep = tableRes.flatMap(x => {
        if (x.subfields) {
            return [...x.subfields.map(y => y.dataIndex), x.dataIndex];
        } else {
            return x.dataIndex
        }
    });
    keysToKeep.push("Conversion");

    const result = tableRes.flatMap(x => {
        var obj = Object.entries(selectedRecord);
        if (x.subfields) {
            var foundObj = obj.find(z => z[0] === x.dataIndex);
            var parentObj = buildObj(foundObj, x, null, true, returnOptions, configValues);
            return [({
                childs: x.subfields.map(y => {
                    var foundObj = obj.find(z => z[0] === y.dataIndex);
                    return buildObj(foundObj, y, true, parentObj.key, returnOptions, configValues);
                }), parent: parentObj
            })];
        } else {
            var foundObj = obj.find(z => z[0] === x.dataIndex);
            return buildObj(foundObj, x, null, null, returnOptions, configValues);
        }
    }).filter(e => e !== undefined);

    var y = result.filter(x => {
        if (x?.parent) {
            for (var i = 0; i < x.childs.length; i++) {
                if (!keysToKeep.includes(x.childs[i].key))
                    delete x.childs[i]
            }
            return keysToKeep.includes(x.parent.key)
        }
        return keysToKeep.includes(x.key)
    });
    return y;
};

function returnOptions(key, configValues) {
    switch (key.toLowerCase()) {
        case 'tenant':
            if (configValues)
                return uniqueObjectsByProperty(configValues[key].result);
        case 'periodicity':
            return periodicity.map(x => { return { lable: x, value: x } });
        case 'period':
            return period.map(x => { return { lable: x, value: x } });
        case 'type':
            return primitiveTypes.map(x => { return { lable: x, value: x } });
        case 'detailSeperation':
        case 'period':
        case 'history':
            return booleans.map(x => { return { label: x, value: x } });
        case 'widgetType':
            return []
        default:
            if (configValues && configValues[key]) {
                if (configValues[key].result) {
                    var x = uniqueObjectsByProperty(configValues[key].result.map(x => { return { label: x, value: x } }), key);
                    return x;
                } else if (Array.isArray(configValues[key])) {
                    var x = uniqueObjectsByProperty(configValues[key]);
                    return x;
                }
            }
            else
                return [];
    }
}

const metricEmptyRecord = {
    "Old Label": null,
    "Old Unit": null,
    Label: null,
    Unit: null,
    Type: "Int",
    Conversion: null,
    Offset: null,
    Power: null,
    Slope: null,
    "Standard Unit": null,
    isStandard: false
}

function uniqueObjectsByProperty(array, property) {
    if (property)
        return Array.from(new Set(array.map(item => item[property]))).map(value => ({ label: value, value: value }));
    else
        return Array.from(new Set(array)).map(value => ({ label: value, value: value }));
}

const serealizeData = (values) => {
    if (values) {
        return values.result.map((e, index) => {
            return {
                id: index,//TODO JORGE: Trocar para o ID (necessário começar a criar um ID único para cada registro)
                oldLabel: e['Label'] ?? null,
                oldUnit: e['Unit'] ?? null,
                Label: e['Label'] ?? null,
                Slope: e['Slope'] ?? null,
                Unit: e['Unit'] ?? null,
                Power: e['Power'] ?? null,
                Conversion: null,
                Offset: e['Offset'] ?? null,
                "Standard Unit": e['Standard Unit'] ?? null,
                Type: e['Type'] ?? null,
                isStandard: null
            }
        })
    }
}